/**
 * Community API
 *
 * Mock community service for social features.
 */

import type {
  Community,
  CommunityMembership,
  CommunityInvitation,
  FeedItem,
  FeedComment,
  CommunityFilters,
  CommunityVisibility,
  CommunityType,
  JoinModel,
  DiscussionPosting,
  CommunityRole,
  SchoolCommunityLevel,
  CommunityAssignment,
  CommunityStudentProgress,
  Challenge,
  UserChallenge,
  User,
  TrackRecord,
  ApiResponse,
  UUID,
} from '../types'
import { COMMUNITY_TYPE_DEFAULTS } from '../types'
import { mockDb, delay } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Community API
 */
export const communityApi = {
  // ============================================================================
  // Communities
  // ============================================================================

  /**
   * Get all communities (with filters)
   */
  async getCommunities(filters?: CommunityFilters): Promise<ApiResponse<Community[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    let communities = mockDb.getAll<Community>('communities')

    // Apply filters
    if (filters?.search) {
      const search = filters.search.toLowerCase()
      communities = communities.filter(
        (c) =>
          c.name.toLowerCase().includes(search) ||
          c.description?.toLowerCase().includes(search)
      )
    }

    if (filters?.visibility) {
      communities = communities.filter((c) => c.visibility === filters.visibility)
    }

    // Filter by type
    if (filters?.type) {
      communities = communities.filter((c) => c.type === filters.type)
    }

    // Filter by multiple types
    if (filters?.types && filters.types.length > 0) {
      communities = communities.filter((c) => filters.types!.includes(c.type))
    }

    // Filter by join model
    if (filters?.joinModel) {
      communities = communities.filter((c) => c.joinModel === filters.joinModel)
    }

    // Filter by school ID
    if (filters?.schoolId) {
      communities = communities.filter((c) => c.schoolId === filters.schoolId)
    }

    // Filter by school community level
    if (filters?.schoolCommunityLevel) {
      communities = communities.filter((c) => c.schoolCommunityLevel === filters.schoolCommunityLevel)
    }

    if (filters?.isMember && userId) {
      const memberships = mockDb.findWhere<CommunityMembership>(
        'communityMemberships',
        (m) => m.userId === userId && m.status === 'active'
      )
      const memberCommunityIds = new Set(memberships.map((m) => m.communityId))
      communities = communities.filter((c) => memberCommunityIds.has(c.id))
    }

    // Populate current user membership
    if (userId) {
      communities = communities.map((c) => ({
        ...c,
        currentUserMembership: mockDb.findOne<CommunityMembership>(
          'communityMemberships',
          (m) => m.communityId === c.id && m.userId === userId
        ) ?? undefined,
      }))
    }

    return success(communities)
  },

  /**
   * Get my communities
   */
  async getMyCommunities(): Promise<ApiResponse<Community[]>> {
    return this.getCommunities({ isMember: true })
  },

  /**
   * Get community by ID
   */
  async getCommunityById(communityId: UUID): Promise<ApiResponse<Community | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    const community = mockDb.findById<Community>('communities', communityId)

    if (!community) {
      return error('Community not found')
    }

    // Populate current user membership
    if (userId) {
      community.currentUserMembership = mockDb.findOne<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === communityId && m.userId === userId
      ) ?? undefined
    }

    return success(community)
  },

  /**
   * Create a community (non-institutional types — institutional communities are created by SAs)
   */
  async createCommunity(data: {
    name: string
    description?: string
    type: CommunityType
    visibility?: CommunityVisibility
    joinModel?: JoinModel
    discussionPosting?: DiscussionPosting
    giftingDefault?: boolean
    avatarUrl?: string
    coverImageUrl?: string
  }): Promise<ApiResponse<Community>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    if (data.type === 'institutional') {
      return error('Institutional communities can only be created by School Admins')
    }

    // Check for duplicate name/slug
    const slug = data.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    const existing = mockDb.findOne<Community>(
      'communities',
      (c) => c.slug === slug
    )

    if (existing) {
      return error('A community with this name already exists')
    }

    // Apply type-based defaults, allow overrides
    const defaults = COMMUNITY_TYPE_DEFAULTS[data.type]

    const community = mockDb.insert<Community>('communities', {
      name: data.name,
      description: data.description,
      slug,
      type: data.type,
      creatorUserId: userId,
      visibility: data.visibility ?? defaults.visibility,
      joinModel: data.joinModel ?? defaults.joinModel,
      discussionPosting: data.discussionPosting ?? 'all_members',
      giftingDefault: data.giftingDefault ?? defaults.giftingDefault,
      avatarUrl: data.avatarUrl,
      coverImageUrl: data.coverImageUrl,
      memberCount: 1,
      postCount: 0,
      status: 'active',
    })

    // Create creator membership
    mockDb.insert<CommunityMembership>('communityMemberships', {
      communityId: community.id,
      userId,
      role: 'creator',
      status: 'active',
      canPost: true,
      canModerate: true,
      canInvite: true,
      joinedAt: new Date().toISOString(),
    })

    return success(community)
  },

  /**
   * Update community
   */
  async updateCommunity(
    communityId: UUID,
    data: {
      name?: string
      description?: string
      visibility?: CommunityVisibility
      joinModel?: JoinModel
      discussionPosting?: DiscussionPosting
      giftingDefault?: boolean
      trackRecordCommentsEnabled?: boolean
      avatarUrl?: string
      coverImageUrl?: string
      bannerUrl?: string
    }
  ): Promise<ApiResponse<Community>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) {
      return error('Community not found')
    }

    // Check permission (creator or admin)
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )

    if (!membership || !['owner', 'admin'].includes(membership.role)) {
      return error('Not authorized')
    }

    const updated = mockDb.update<Community>('communities', communityId, data)
    if (!updated) {
      return error('Failed to update community')
    }

    return success(updated)
  },

  /**
   * Join a community
   */
  async joinCommunity(communityId: UUID): Promise<ApiResponse<CommunityMembership>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) {
      return error('Community not found')
    }

    // Check if already a member
    const existing = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )

    if (existing) {
      if (existing.status === 'active') {
        return error('Already a member')
      }
      if (existing.status === 'removed') {
        return error('You have been removed from this community')
      }
    }

    // Determine status based on join model
    let status: 'active' | 'pending'
    if (community.joinModel === 'auto_join') {
      status = 'active'
    } else if (community.joinModel === 'requestable') {
      status = 'pending'
    } else if (community.joinModel === 'invite_only') {
      return error('This community is invite-only')
    } else {
      return error('Membership is managed by the administrator')
    }

    const canPost = community.discussionPosting === 'all_members'

    const membership = mockDb.insert<CommunityMembership>('communityMemberships', {
      communityId,
      userId,
      role: 'member',
      status,
      canPost,
      canModerate: false,
      canInvite: false,
      joinedAt: status === 'active' ? new Date().toISOString() : undefined,
    })

    // Update member count
    if (status === 'active') {
      mockDb.update<Community>('communities', communityId, {
        memberCount: community.memberCount + 1,
      })
    }

    return success(membership)
  },

  /**
   * Leave a community
   */
  async leaveCommunity(communityId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )

    if (!membership) {
      return error('Not a member')
    }

    if (membership.role === 'creator') {
      return error('Creator cannot leave community without transferring ownership first')
    }

    mockDb.delete('communityMemberships', membership.id)

    // Update member count
    const community = mockDb.findById<Community>('communities', communityId)
    if (community) {
      mockDb.update<Community>('communities', communityId, {
        memberCount: Math.max(0, community.memberCount - 1),
      })
    }

    return success(undefined)
  },

  /**
   * Get community members
   */
  async getCommunityMembers(
    communityId: UUID,
    page = 1,
    limit = 20
  ): Promise<ApiResponse<CommunityMembership[]>> {
    await delay(50)

    const community = mockDb.findById<Community>('communities', communityId)

    const memberships = mockDb
      .findWhere<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === communityId && m.status === 'active'
      )
      .sort((a, b) => {
        const roleOrder = { creator: 0, owner: 0, admin: 1, moderator: 2, member: 3 }
        return (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3)
      })

    // Populate user data from the users collection
    const populatedMemberships = memberships.map((m) => {
      const userRecord = mockDb.findById<{ id: string; displayName: string; avatarUrl?: string }>('users', m.userId)
      return {
        ...m,
        user: userRecord
          ? { id: userRecord.id, displayName: userRecord.displayName, avatarUrl: userRecord.avatarUrl }
          : { id: m.userId, displayName: `User ${m.userId.slice(0, 6)}` },
      }
    })

    // Generate placeholder members if memberCount exceeds actual memberships
    const targetCount = community?.memberCount ?? memberships.length
    const placeholderNames = [
      'Aiden Chen', 'Mia Ramirez', 'Noah Patel', 'Isla Kim', 'Lucas Morgan',
      'Zoe Nakamura', 'Ethan Torres', 'Ava Singh', 'Liam O\'Brien', 'Harper Lee',
      'Mason Dubois', 'Ella Kowalski', 'Oliver Tanaka', 'Chloe Martinez', 'Jack Wu',
      'Lily Anderson', 'Henry Park', 'Grace Thompson', 'Leo Fernandez', 'Sofia Hassan',
      'Ben Cooper', 'Nora Jensen', 'Sam Rivera', 'Maya Scott', 'Owen Blake',
    ]
    const allMembers = [...populatedMemberships]
    for (let i = populatedMemberships.length; i < targetCount && i - populatedMemberships.length < placeholderNames.length; i++) {
      const idx = i - populatedMemberships.length
      const name = placeholderNames[idx]
      allMembers.push({
        id: `placeholder-${communityId}-${idx}`,
        communityId,
        userId: `placeholder-user-${idx}`,
        role: 'member',
        status: 'active',
        canPost: true,
        canModerate: false,
        canInvite: false,
        joinedAt: community?.createdAt ?? new Date().toISOString(),
        createdAt: community?.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: { id: `placeholder-user-${idx}`, displayName: name },
      } as CommunityMembership)
    }

    // Paginate
    const start = (page - 1) * limit
    const paginatedMemberships = allMembers.slice(start, start + limit)

    return success(paginatedMemberships)
  },

  /**
   * Update member role
   */
  async updateMemberRole(
    membershipId: UUID,
    role: CommunityRole
  ): Promise<ApiResponse<CommunityMembership>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const membership = mockDb.findById<CommunityMembership>(
      'communityMemberships',
      membershipId
    )
    if (!membership) {
      return error('Membership not found')
    }

    // Check permission
    const userMembership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === membership.communityId && m.userId === userId
    )

    if (!userMembership || !['creator', 'admin'].includes(userMembership.role)) {
      return error('Not authorized')
    }

    // Can't change creator role
    if (membership.role === 'creator') {
      return error('Cannot change creator role')
    }

    const updated = mockDb.update<CommunityMembership>(
      'communityMemberships',
      membershipId,
      {
        role,
        canPost: true,
        canModerate: ['creator', 'admin', 'moderator'].includes(role),
        canInvite: ['creator', 'admin'].includes(role),
      }
    )

    if (!updated) {
      return error('Failed to update role')
    }

    return success(updated)
  },

  /**
   * Update the current user's gifting permission override for a community.
   * Pass null to reset to community default.
   */
  async updateMyGiftingOverride(
    communityId: UUID,
    override: 'allowed' | 'blocked' | null
  ): Promise<ApiResponse<CommunityMembership>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId && m.status === 'active'
    )

    if (!membership) {
      return error('Not a member of this community')
    }

    const updated = mockDb.update<CommunityMembership>(
      'communityMemberships',
      membership.id,
      { giftingPermissionOverride: override }
    )

    if (!updated) {
      return error('Failed to update gifting preference')
    }

    return success(updated)
  },

  // ============================================================================
  // Feed
  // ============================================================================

  /**
   * Get community feed
   */
  async getCommunityFeed(
    communityId: UUID,
    page = 1,
    limit = 20
  ): Promise<ApiResponse<FeedItem[]>> {
    await delay(100)

    const feedItems = mockDb
      .findWhere<FeedItem>('communityFeed', (f) => f.communityId === communityId)
      .sort((a, b) => {
        // Pinned items first, then by date
        if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })

    // Paginate
    const start = (page - 1) * limit
    const paginatedItems = feedItems.slice(start, start + limit)

    // Populate challenge data for challenge_share items
    const populatedItems = paginatedItems.map((item) => {
      if (item.challengeId && !item.challenge) {
        return {
          ...item,
          challenge: mockDb.findById<Challenge>('challenges', item.challengeId) ?? undefined,
        }
      }
      return item
    })

    return success(populatedItems)
  },

  /**
   * Create a feed post
   */
  async createFeedPost(
    communityId: UUID,
    data: {
      textContent?: string
      trackRecordId?: UUID
      challengeId?: UUID
      type?: 'track_record' | 'discussion' | 'challenge_share'
    }
  ): Promise<ApiResponse<FeedItem>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Check membership and posting permission
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )

    if (!membership || membership.status !== 'active') {
      return error('Not a member of this community')
    }

    if (!membership.canPost) {
      return error('You do not have permission to post')
    }

    const feedItem = mockDb.insert<FeedItem>('communityFeed', {
      communityId,
      authorUserId: userId,
      type: data.type ?? 'discussion',
      textContent: data.textContent,
      trackRecordId: data.trackRecordId,
      challengeId: data.challengeId,
      isPinned: false,
      isAnnouncement: false,
      likeCount: 0,
      commentCount: 0,
    })

    // Update community post count
    const community = mockDb.findById<Community>('communities', communityId)
    if (community) {
      mockDb.update<Community>('communities', communityId, {
        postCount: community.postCount + 1,
      })
    }

    return success(feedItem)
  },

  /**
   * Delete a feed post
   */
  async deleteFeedPost(feedItemId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const feedItem = mockDb.findById<FeedItem>('communityFeed', feedItemId)
    if (!feedItem) {
      return error('Post not found')
    }

    // Check permission (author or moderator)
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === feedItem.communityId && m.userId === userId
    )

    if (feedItem.authorUserId !== userId && !membership?.canModerate) {
      return error('Not authorized')
    }

    mockDb.delete('communityFeed', feedItemId)

    // Update community post count
    const community = mockDb.findById<Community>('communities', feedItem.communityId)
    if (community) {
      mockDb.update<Community>('communities', feedItem.communityId, {
        postCount: Math.max(0, community.postCount - 1),
      })
    }

    return success(undefined)
  },

  /**
   * Like a feed post
   */
  async likeFeedPost(feedItemId: UUID): Promise<ApiResponse<boolean>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const feedItem = mockDb.findById<FeedItem>('communityFeed', feedItemId)
    if (!feedItem) {
      return error('Post not found')
    }

    // Just increment like count (simplified - real impl would track individual likes)
    mockDb.update<FeedItem>('communityFeed', feedItemId, {
      likeCount: feedItem.likeCount + 1,
    })

    return success(true)
  },

  /**
   * Add comment to feed post
   */
  async addFeedComment(
    feedItemId: UUID,
    content: string,
    parentCommentId?: UUID
  ): Promise<ApiResponse<FeedComment>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const feedItem = mockDb.findById<FeedItem>('communityFeed', feedItemId)
    if (!feedItem) {
      return error('Post not found')
    }

    const comment = mockDb.insert<FeedComment>('feedComments', {
      feedItemId,
      authorUserId: userId,
      parentCommentId,
      content,
    })

    // Update comment count
    mockDb.update<FeedItem>('communityFeed', feedItemId, {
      commentCount: feedItem.commentCount + 1,
    })

    return success(comment)
  },

  /**
   * Get comments for a feed post
   */
  async getFeedComments(feedItemId: UUID): Promise<ApiResponse<FeedComment[]>> {
    await delay(50)

    const comments = mockDb
      .findWhere<FeedComment>('feedComments', (c) => c.feedItemId === feedItemId)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

    return success(comments)
  },

  // ============================================================================
  // Batch Share
  // ============================================================================

  /**
   * Share track record to multiple communities
   */
  async batchShareTrackRecord(
    trackRecordId: UUID,
    communityIds: UUID[],
    textContent?: string
  ): Promise<ApiResponse<FeedItem[]>> {
    await delay(200)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const feedItems: FeedItem[] = []

    for (const communityId of communityIds) {
      // Verify membership
      const membership = mockDb.findOne<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === communityId && m.userId === userId && m.status === 'active'
      )

      if (membership && membership.canPost) {
        const feedItem = mockDb.insert<FeedItem>('communityFeed', {
          communityId,
          authorUserId: userId,
          type: 'track_record',
          textContent,
          trackRecordId,
          isPinned: false,
          isAnnouncement: false,
          likeCount: 0,
          commentCount: 0,
        })

        feedItems.push(feedItem)

        // Update post count
        const community = mockDb.findById<Community>('communities', communityId)
        if (community) {
          mockDb.update<Community>('communities', communityId, {
            postCount: community.postCount + 1,
          })
        }
      }
    }

    return success(feedItems)
  },

  // ============================================================================
  // School Communities
  // ============================================================================

  /**
   * Get school communities for a specific school
   */
  async getSchoolCommunities(schoolId: UUID): Promise<ApiResponse<Community[]>> {
    return this.getCommunities({ type: 'institutional', schoolId })
  },

  /**
   * Get user's school community by level (full_school, grade, or class)
   * This returns the specific community for the current user's school/grade/class
   */
  async getSchoolCommunityByLevel(
    schoolId: UUID,
    level: SchoolCommunityLevel,
    gradeLevel?: string,
    classId?: UUID
  ): Promise<ApiResponse<Community | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    let community: Community | null = null

    if (level === 'full_school') {
      const found = mockDb.findOne<Community>(
        'communities',
        (c) => c.type === 'institutional' && c.schoolId === schoolId && c.schoolCommunityLevel === 'full_school'
      )
      community = found ?? null
    } else if (level === 'grade' && gradeLevel) {
      const found = mockDb.findOne<Community>(
        'communities',
        (c) => c.type === 'institutional' && c.schoolId === schoolId && c.schoolCommunityLevel === 'grade' && c.gradeLevel === gradeLevel
      )
      community = found ?? null
    } else if (level === 'class' && classId) {
      const found = mockDb.findOne<Community>(
        'communities',
        (c) => c.type === 'institutional' && c.schoolId === schoolId && c.schoolCommunityLevel === 'class' && c.classId === classId
      )
      community = found ?? null
    }

    if (community && userId) {
      // Populate current user membership
      community.currentUserMembership = mockDb.findOne<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === community!.id && m.userId === userId
      ) ?? undefined
    }

    return success(community)
  },

  /**
   * Personal community types (all non-institutional)
   */
  _personalTypes: ['family', 'trusted_friends', 'casual_friends', 'interest_hobby', 'other'] as CommunityType[],

  /**
   * Get personal communities (user-created, non-institutional)
   */
  async getPersonalCommunities(): Promise<ApiResponse<Community[]>> {
    return this.getCommunities({ types: this._personalTypes, isMember: true })
  },

  /**
   * Get all discoverable communities (for discovery page)
   */
  async getDiscoverableCommunities(): Promise<ApiResponse<Community[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    let communities = mockDb.findWhere<Community>(
      'communities',
      (c) => c.type !== 'institutional' && c.visibility === 'public' && c.status === 'active'
    )

    // Populate current user membership
    if (userId) {
      communities = communities.map((c) => ({
        ...c,
        currentUserMembership: mockDb.findOne<CommunityMembership>(
          'communityMemberships',
          (m) => m.communityId === c.id && m.userId === userId
        ) ?? undefined,
      }))
    }

    return success(communities)
  },

  // ============================================================================
  // Challenge Assignments (for class communities)
  // ============================================================================

  /**
   * Assign a challenge to a community
   * Only teachers/admins can assign challenges to class communities
   */
  async assignChallenge(
    communityId: UUID,
    challengeId: UUID,
    data: {
      dueDate?: string
      assignmentNote?: string
    }
  ): Promise<ApiResponse<CommunityAssignment>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) {
      return error('Community not found')
    }

    // Verify this is a class community
    if (community.type !== 'institutional' || community.schoolCommunityLevel !== 'class') {
      return error('Challenges can only be assigned to class communities')
    }

    // Check if user has permission (teacher, admin, or creator)
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )

    if (!membership || !['creator', 'admin', 'moderator'].includes(membership.role)) {
      return error('Not authorized to assign challenges')
    }

    // Check if challenge exists
    const challenge = mockDb.findById<Challenge>('challenges', challengeId)
    if (!challenge) {
      return error('Challenge not found')
    }

    // Archive any existing active assignment for this community
    const existingAssignments = mockDb.findWhere<CommunityAssignment>(
      'communityAssignments',
      (a) => a.communityId === communityId && a.status === 'active'
    )
    for (const existing of existingAssignments) {
      mockDb.update<CommunityAssignment>('communityAssignments', existing.id, {
        status: 'archived',
      })
    }

    // Get all students (members) in the community
    const members = mockDb.findWhere<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.status === 'active' && m.role === 'member'
    )

    // Create the assignment
    const assignment = mockDb.insert<CommunityAssignment>('communityAssignments', {
      communityId,
      challengeId,
      assignedByUserId: userId,
      dueDate: data.dueDate,
      assignmentNote: data.assignmentNote,
      status: 'active',
      totalStudents: members.length,
      completedCount: 0,
      inProgressCount: 0,
      submittedCount: 0,
    })

    // Auto-enroll all students in the challenge
    for (const member of members) {
      // Check if user already has this challenge
      const existingUserChallenge = mockDb.findOne<UserChallenge>(
        'userChallenges',
        (uc) => uc.userId === member.userId && uc.challengeId === challengeId && uc.status !== 'abandoned'
      )

      if (!existingUserChallenge) {
        // Create assigned user challenge
        const userChallenge = mockDb.insert<UserChallenge>('userChallenges', {
          userId: member.userId,
          challengeId,
          status: 'assigned',
          source: 'class_assignment',
          communityAssignmentId: assignment.id,
          communityId,
          communityName: community?.name,
          completionNumber: 1,
          verificationAttempts: 0,
          maxAttempts: 3,
        })

        // Create student progress entry
        mockDb.insert<CommunityStudentProgress>('studentAssignmentProgress', {
          assignmentId: assignment.id,
          userId: member.userId,
          userChallengeId: userChallenge.id,
          status: 'not_started',
          progressPercent: 0,
        })
      } else {
        // Update existing UserChallenge to link to this assignment if not already linked
        if (!existingUserChallenge.communityAssignmentId) {
          mockDb.update<UserChallenge>('userChallenges', existingUserChallenge.id, {
            source: 'class_assignment',
            communityAssignmentId: assignment.id,
            communityId,
            communityName: community?.name,
          })
        }

        // Create progress entry for existing challenge
        const status = existingUserChallenge.status === 'verified' ? 'completed' :
                      existingUserChallenge.status === 'submitted' ? 'submitted' :
                      existingUserChallenge.status === 'in_progress' ? 'in_progress' : 'not_started'

        mockDb.insert<CommunityStudentProgress>('studentAssignmentProgress', {
          assignmentId: assignment.id,
          userId: member.userId,
          userChallengeId: existingUserChallenge.id,
          status,
          progressPercent: status === 'completed' ? 100 : status === 'submitted' ? 90 : status === 'in_progress' ? 50 : 0,
          startedAt: existingUserChallenge.startedAt,
          submittedAt: existingUserChallenge.submittedAt,
          completedAt: existingUserChallenge.verifiedAt,
        })
      }
    }

    // Populate challenge
    assignment.challenge = challenge

    return success(assignment)
  },

  /**
   * Get current (active) assignment for a community
   */
  async getCurrentAssignment(communityId: UUID): Promise<ApiResponse<CommunityAssignment | null>> {
    await delay(50)

    const assignment = mockDb.findOne<CommunityAssignment>(
      'communityAssignments',
      (a) => a.communityId === communityId && a.status === 'active'
    )

    if (assignment) {
      // Populate challenge
      assignment.challenge = mockDb.findById<Challenge>('challenges', assignment.challengeId) ?? undefined
    }

    return success(assignment)
  },

  /**
   * Get all assignments for a community (including archived)
   */
  async getCommunityAssignments(
    communityId: UUID,
    status?: 'active' | 'completed' | 'archived'
  ): Promise<ApiResponse<CommunityAssignment[]>> {
    await delay(100)

    let assignments = mockDb.findWhere<CommunityAssignment>(
      'communityAssignments',
      (a) => a.communityId === communityId
    )

    if (status) {
      assignments = assignments.filter((a) => a.status === status)
    }

    // Sort by creation date, newest first
    assignments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Populate challenges
    assignments = assignments.map((a) => ({
      ...a,
      challenge: mockDb.findById<Challenge>('challenges', a.challengeId) ?? undefined,
    }))

    return success(assignments)
  },

  /**
   * Get student progress for an assignment
   */
  async getAssignmentProgress(assignmentId: UUID): Promise<ApiResponse<CommunityStudentProgress[]>> {
    await delay(100)

    const progress = mockDb.findWhere<CommunityStudentProgress>(
      'studentAssignmentProgress',
      (p) => p.assignmentId === assignmentId
    )

    // Populate user info and track record IDs
    const assignment = mockDb.findById<CommunityAssignment>('communityAssignments', assignmentId)

    const populatedProgress = progress.map((p) => {
      // Try to find actual user record
      const userRecord = mockDb.findById<User>('users', p.userId)

      // Try to find a real track record, or assign a deterministic mock ID
      let trackRecordId = p.trackRecordId
      if (!trackRecordId && assignment && p.status !== 'not_started') {
        const tr = mockDb.findOne<TrackRecord>(
          'trackRecords',
          (t) => t.userId === p.userId && t.challengeId === assignment.challengeId
        )
        trackRecordId = tr?.id ?? `mock-tr-${p.userId}-${assignment.challengeId}`
      }

      return {
        ...p,
        trackRecordId,
        user: userRecord
          ? {
              id: userRecord.id,
              displayName: userRecord.displayName || `${userRecord.profile?.firstName || ''} ${userRecord.profile?.lastName || ''}`.trim() || userRecord.email,
              avatarUrl: userRecord.avatarUrl,
            }
          : {
              id: p.userId,
              displayName: 'Student',
            },
      }
    })

    // Sort by status (completed first, then submitted, in_progress, not_started)
    const statusOrder = { completed: 0, submitted: 1, in_progress: 2, not_started: 3 }
    populatedProgress.sort((a, b) => statusOrder[a.status] - statusOrder[b.status])

    return success(populatedProgress)
  },

  /**
   * Update assignment (e.g., extend due date, update note)
   */
  async updateAssignment(
    assignmentId: UUID,
    data: {
      dueDate?: string
      assignmentNote?: string
      status?: 'active' | 'completed' | 'archived'
    }
  ): Promise<ApiResponse<CommunityAssignment>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const assignment = mockDb.findById<CommunityAssignment>('communityAssignments', assignmentId)
    if (!assignment) {
      return error('Assignment not found')
    }

    // Check permission
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === assignment.communityId && m.userId === userId
    )

    if (!membership || !['creator', 'admin', 'moderator'].includes(membership.role)) {
      return error('Not authorized')
    }

    const updated = mockDb.update<CommunityAssignment>('communityAssignments', assignmentId, data)
    if (!updated) {
      return error('Failed to update assignment')
    }

    // Populate challenge
    updated.challenge = mockDb.findById<Challenge>('challenges', updated.challengeId) ?? undefined

    return success(updated)
  },

  /**
   * Archive an assignment
   */
  async archiveAssignment(assignmentId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const assignment = mockDb.findById<CommunityAssignment>('communityAssignments', assignmentId)
    if (!assignment) {
      return error('Assignment not found')
    }

    // Check permission
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === assignment.communityId && m.userId === userId
    )

    if (!membership || !['creator', 'admin', 'moderator'].includes(membership.role)) {
      return error('Not authorized')
    }

    mockDb.update<CommunityAssignment>('communityAssignments', assignmentId, {
      status: 'archived',
    })

    return success(undefined)
  },

  // ============================================================================
  // Invitations
  // ============================================================================

  /**
   * Send an invitation to join a community
   */
  async sendInvitation(
    communityId: UUID,
    data: { userId?: UUID; email?: string }
  ): Promise<ApiResponse<CommunityInvitation>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) return error('Community not found')

    // Check permission (must be member with canInvite, or creator/admin/moderator)
    const membership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId && m.status === 'active'
    )

    if (!membership || (!membership.canInvite && !['creator', 'admin', 'moderator'].includes(membership.role))) {
      return error('Not authorized to invite members')
    }

    // Check if target user is already a member
    if (data.userId) {
      const existing = mockDb.findOne<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === communityId && m.userId === data.userId && m.status === 'active'
      )
      if (existing) return error('User is already a member')
    }

    // Check for existing pending invitation
    const existingInvite = mockDb.findOne<CommunityInvitation>(
      'communityInvitations',
      (inv) =>
        inv.communityId === communityId &&
        inv.status === 'pending' &&
        ((data.userId && inv.invitedUserId === data.userId) ||
          (data.email && inv.invitedEmail === data.email))
    )
    if (existingInvite) return error('An invitation is already pending for this person')

    const invitation = mockDb.insert<CommunityInvitation>('communityInvitations', {
      communityId,
      invitedUserId: data.userId,
      invitedEmail: data.email,
      invitedByUserId: userId,
      status: 'pending',
      expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    })

    // Populate community
    invitation.community = community

    return success(invitation)
  },

  /**
   * Get invitations the current user has received (pending)
   */
  async getMyPendingInvitations(): Promise<ApiResponse<CommunityInvitation[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const invitations = mockDb.findWhere<CommunityInvitation>(
      'communityInvitations',
      (inv) => inv.invitedUserId === userId && inv.status === 'pending'
    )

    // Populate community
    const populated = invitations.map((inv) => ({
      ...inv,
      community: mockDb.findById<Community>('communities', inv.communityId) ?? undefined,
    }))

    return success(populated)
  },

  /**
   * Get outbound invitations for a community (for admins)
   */
  async getCommunityInvitations(communityId: UUID): Promise<ApiResponse<CommunityInvitation[]>> {
    await delay(50)

    const invitations = mockDb
      .findWhere<CommunityInvitation>(
        'communityInvitations',
        (inv) => inv.communityId === communityId
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return success(invitations)
  },

  /**
   * Accept a community invitation
   */
  async acceptInvitation(invitationId: UUID): Promise<ApiResponse<CommunityMembership>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const invitation = mockDb.findById<CommunityInvitation>('communityInvitations', invitationId)
    if (!invitation) return error('Invitation not found')
    if (invitation.invitedUserId !== userId) return error('Not authorized')
    if (invitation.status !== 'pending') return error('Invitation is no longer pending')

    // Update invitation
    mockDb.update<CommunityInvitation>('communityInvitations', invitationId, {
      status: 'accepted',
    })

    const community = mockDb.findById<Community>('communities', invitation.communityId)

    // Create membership
    const canPost = community?.discussionPosting === 'all_members'
    const membership = mockDb.insert<CommunityMembership>('communityMemberships', {
      communityId: invitation.communityId,
      userId,
      role: 'member',
      status: 'active',
      canPost,
      canModerate: false,
      canInvite: false,
      joinedAt: new Date().toISOString(),
      invitedAt: invitation.createdAt,
      invitedByUserId: invitation.invitedByUserId,
    })

    // Update member count
    if (community) {
      mockDb.update<Community>('communities', invitation.communityId, {
        memberCount: community.memberCount + 1,
      })
    }

    return success(membership)
  },

  /**
   * Decline a community invitation
   */
  async declineInvitation(invitationId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const invitation = mockDb.findById<CommunityInvitation>('communityInvitations', invitationId)
    if (!invitation) return error('Invitation not found')
    if (invitation.invitedUserId !== userId) return error('Not authorized')
    if (invitation.status !== 'pending') return error('Invitation is no longer pending')

    mockDb.update<CommunityInvitation>('communityInvitations', invitationId, {
      status: 'declined',
    })

    return success(undefined)
  },

  /**
   * Cancel an outbound invitation (for the inviter or admin)
   */
  async cancelInvitation(invitationId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const invitation = mockDb.findById<CommunityInvitation>('communityInvitations', invitationId)
    if (!invitation) return error('Invitation not found')
    if (invitation.status !== 'pending') return error('Invitation is no longer pending')

    // Check permission (inviter or community admin)
    if (invitation.invitedByUserId !== userId) {
      const membership = mockDb.findOne<CommunityMembership>(
        'communityMemberships',
        (m) => m.communityId === invitation.communityId && m.userId === userId
      )
      if (!membership || !['creator', 'admin'].includes(membership.role)) {
        return error('Not authorized')
      }
    }

    mockDb.update<CommunityInvitation>('communityInvitations', invitationId, {
      status: 'expired',
    })

    return success(undefined)
  },

  // ============================================================================
  // Join Requests (for requestable communities)
  // ============================================================================

  /**
   * Request to join a requestable community (creates a pending membership)
   */
  async requestToJoin(communityId: UUID): Promise<ApiResponse<CommunityMembership>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const community = mockDb.findById<Community>('communities', communityId)
    if (!community) return error('Community not found')

    if (community.joinModel !== 'requestable') {
      return error('This community does not accept join requests')
    }

    // Check existing membership
    const existing = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.userId === userId
    )

    if (existing) {
      if (existing.status === 'active') return error('Already a member')
      if (existing.status === 'pending') return error('You already have a pending request')
      if (existing.status === 'removed') return error('You have been removed from this community')
    }

    const membership = mockDb.insert<CommunityMembership>('communityMemberships', {
      communityId,
      userId,
      role: 'member',
      status: 'pending',
      canPost: false,
      canModerate: false,
      canInvite: false,
    })

    return success(membership)
  },

  /**
   * Get pending join requests for a community (for moderators)
   */
  async getPendingJoinRequests(communityId: UUID): Promise<ApiResponse<CommunityMembership[]>> {
    await delay(50)

    const requests = mockDb.findWhere<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === communityId && m.status === 'pending'
    )

    return success(requests)
  },

  /**
   * Approve a join request
   */
  async approveJoinRequest(membershipId: UUID): Promise<ApiResponse<CommunityMembership>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const membership = mockDb.findById<CommunityMembership>('communityMemberships', membershipId)
    if (!membership) return error('Request not found')
    if (membership.status !== 'pending') return error('Request is no longer pending')

    // Check permission
    const userMembership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === membership.communityId && m.userId === userId
    )
    if (!userMembership || !['creator', 'admin', 'moderator'].includes(userMembership.role)) {
      return error('Not authorized')
    }

    const community = mockDb.findById<Community>('communities', membership.communityId)
    const canPost = community?.discussionPosting === 'all_members'

    const updated = mockDb.update<CommunityMembership>('communityMemberships', membershipId, {
      status: 'active',
      canPost,
      joinedAt: new Date().toISOString(),
    })

    if (!updated) return error('Failed to approve request')

    // Update member count
    if (community) {
      mockDb.update<Community>('communities', membership.communityId, {
        memberCount: community.memberCount + 1,
      })
    }

    return success(updated)
  },

  /**
   * Reject a join request
   */
  async rejectJoinRequest(membershipId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) return error('Not authenticated')

    const membership = mockDb.findById<CommunityMembership>('communityMemberships', membershipId)
    if (!membership) return error('Request not found')
    if (membership.status !== 'pending') return error('Request is no longer pending')

    // Check permission
    const userMembership = mockDb.findOne<CommunityMembership>(
      'communityMemberships',
      (m) => m.communityId === membership.communityId && m.userId === userId
    )
    if (!userMembership || !['creator', 'admin', 'moderator'].includes(userMembership.role)) {
      return error('Not authorized')
    }

    mockDb.delete('communityMemberships', membershipId)

    return success(undefined)
  },
}
